import axios,{ type AxiosRequestConfig } from 'axios'

const API_PROXY_PREFIX = '/api/'

const normalizePath = (path: string): string => {
  if (path.startsWith('/')) return path
  return `/${path}`
}

export const resolveUrl = (path: string): string => {
  const normalizedPath = normalizePath(path)

  // Image uploads → the rootpoint-media Worker (R2 + cdn.rootpoint.io).
  if (normalizedPath === '/api/media/upload') {
    return import.meta.env.VITE_MEDIA_UPLOAD_URL || 'https://services.rootpoint.io/media/upload'
  }

  if (normalizedPath.startsWith(API_PROXY_PREFIX)) {
    return normalizedPath
  }

  return `${import.meta.env.VITE_API_URL}${normalizedPath}`
}

/**
 * Legacy endpoints sometimes return the payload bare and sometimes wrapped in
 * `{ data: payload }`. Typing a call site as `MaybeEnveloped<T>` lets the
 * established `res?.data ?? res` unwrap narrow to `T` without any runtime
 * change. New code should type the response shape exactly instead.
 */
export type MaybeEnveloped<T> = T & { data?: T }

/** List-endpoint envelope: array payload (extracted via extractArray) + pagination meta. */
export interface ListEnvelope {
  meta?: { page?: number; total?: number; totalPages?: number; totalItems?: number }
  [key: string]: unknown
}

export const api = {
  get: <T = unknown>(path: string, config?: AxiosRequestConfig) =>
    axios.get<T>(resolveUrl(path), config).then((response) => response.data),
  post: <T = unknown>(path: string, data?: unknown, config?: AxiosRequestConfig) =>
    axios.post<T>(resolveUrl(path), data, config).then((response) => response.data),
  patch: <T = unknown>(path: string, data?: unknown, config?: AxiosRequestConfig) =>
    axios.patch<T>(resolveUrl(path), data, config).then((response) => response.data),
  put: <T = unknown>(path: string, data?: unknown, config?: AxiosRequestConfig) =>
    axios.put<T>(resolveUrl(path), data, config).then((response) => response.data),
  delete: <T = unknown>(path: string, config?: AxiosRequestConfig) =>
    axios.delete<T>(resolveUrl(path), config).then((response) => response.data),
}

export const resolveWsUrl = (path: string): string => {
  const base = import.meta.env.VITE_API_URL as string
  const wsBase = base.replace(/^http/, 'ws')
  return `${wsBase}${normalizePath(path)}`
}

export const catalogApi = {
  get: <T = unknown>(path: string, config?: AxiosRequestConfig) =>
    axios
      .get<T>(`${import.meta.env.CATALOG_API_URL || 'https://catalog-dev.rootpoint.io'}${normalizePath(path)}`, config)
      .then((response) => response.data),
}

export const axiosInstance = axios

export type { AxiosError } from 'axios'
export { isAxiosError } from 'axios'
export { isCancel } from 'axios'
